import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { CalendarCheck, CalendarDays, CalendarOff, HandCoins, Settings2, Users, Wallet } from 'lucide-react'
import apiClient from '@/api/client'
import { useCurrency } from '@/contexts/CurrencyContext'
import { getStoredUserRole } from '@/lib/staff-roles'
import { cn } from '@/lib/utils'
import type { HROverview } from '@/types/hr'
import { Stat } from './shared'
import { RegisterTab } from './RegisterTab'
import { MonthTab } from './MonthTab'
import { PeopleTab } from './PeopleTab'
import { LeaveTab } from './LeaveTab'
import { AdvancesTab } from './AdvancesTab'
import { PayrollTab } from './PayrollTab'
import { SetupTab } from './SetupTab'

// Staff & Payroll (F-HR-N1..N6): one page, Reports-style tabs. Managers get
// the register, the month, people and leave; admin and accountant also get
// money; Setup is the admin's.

export type HRTab = 'today' | 'month' | 'people' | 'leave' | 'advances' | 'payroll' | 'setup'

export interface HRSearch {
  tab?: HRTab
  date?: string
  month?: string
  run?: string
  person?: string
}

const TABS: { id: HRTab; label: string; icon: React.ReactNode; pay?: boolean; admin?: boolean }[] = [
  { id: 'today', label: 'Register', icon: <CalendarCheck className="h-4 w-4" /> },
  { id: 'month', label: 'Month', icon: <CalendarDays className="h-4 w-4" /> },
  { id: 'people', label: 'People', icon: <Users className="h-4 w-4" /> },
  { id: 'leave', label: 'Leave', icon: <CalendarOff className="h-4 w-4" /> },
  { id: 'advances', label: 'Advances', icon: <HandCoins className="h-4 w-4" />, pay: true },
  { id: 'payroll', label: 'Payroll', icon: <Wallet className="h-4 w-4" />, pay: true },
  { id: 'setup', label: 'Setup', icon: <Settings2 className="h-4 w-4" />, admin: true },
]

export function hrRole() {
  const role = getStoredUserRole()
  return {
    role,
    isAdmin: role === 'admin',
    seesPay: role === 'admin' || role === 'accountant',
    canMark: role === 'admin' || role === 'manager' || role === 'kitchen_manager',
  }
}

export function HRPage({ search }: { search: HRSearch }) {
  const navigate = useNavigate()
  const { formatCurrency } = useCurrency()
  const { isAdmin, seesPay } = hrRole()
  const tabs = TABS.filter((t) => (!t.pay || seesPay) && (!t.admin || isAdmin))
  const tab = tabs.some((t) => t.id === search.tab) ? (search.tab as HRTab) : 'today'
  const go = (next: Partial<HRSearch>, replace = false) => navigate({ to: '/admin/hr', search: { ...search, ...next }, replace })

  const { data } = useQuery({ queryKey: ['hr', 'overview'], queryFn: () => apiClient.hrOverview(), refetchInterval: 60_000 })
  const ov = data?.data as HROverview | undefined

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Staff &amp; Payroll</h1>
          <p className="mt-1 text-muted-foreground">
            {ov ? `${ov.marked_today} of ${ov.people} marked today` : 'Attendance, leave, advances and salaries in one place'}
          </p>
        </div>
        {seesPay && ov && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="Salary still owed" value={formatCurrency(Math.max(0, ov.salary_due ?? 0))} />
            <Stat
              label="Advances outstanding"
              value={formatCurrency(ov.advances_outstanding ?? 0)}
              hint={ov.open_advances ? `${ov.open_advances} open` : undefined}
            />
            <Stat
              label="Waiting for approval"
              value={ov.runs_waiting_approval ?? 0}
              tone={(ov.runs_waiting_approval ?? 0) > 0 ? 'warn' : undefined}
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => go({ tab: t.id, run: undefined, person: undefined })}
            className={cn(
              'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'today' && <RegisterTab date={search.date} onDate={(date) => go({ date }, true)} />}
      {tab === 'month' && (
        <MonthTab month={search.month} onMonth={(month) => go({ month }, true)} onOpenDay={(date) => go({ tab: 'today', date })} />
      )}
      {tab === 'people' && <PeopleTab personId={search.person} onPerson={(person) => go({ person })} />}
      {tab === 'leave' && <LeaveTab />}
      {tab === 'advances' && <AdvancesTab />}
      {tab === 'payroll' && <PayrollTab runId={search.run} onRun={(run) => go({ run })} onOpenRegister={(date) => go({ tab: 'today', date })} />}
      {tab === 'setup' && <SetupTab />}
    </div>
  )
}
